import { DropdownOptionType } from '@Components/Dropdown/Type';
import { postFedExDetails } from '@Reducers/AdminShipment/actions';
import { AppDispatch } from '@Store';
import dayjs, { Dayjs } from 'dayjs';
import * as Yup from 'yup';

// Payment Mode options
export const paymentModeOptions = [
  { value: 'PX', display: 'PX' },
  { value: 'PD', display: 'PD' },
];

// Service Type options
export const serviceTypeOptions = [
  { value: 'EXE', display: 'EXE' },
  { value: 'EKX', display: 'EKX' },
  { value: 'EKF', display: 'EKF' },
];

export const shipmentPurposeOptions = [
  { value: 'Commercial', display: 'Commercial' },
  { value: 'Gift', display: 'Gift' },
  { value: 'Sample', display: 'Sample' },
  { value: 'PersonalEffects', display: 'Personal Effects' },
];

export const shipmentTypeOptions = [
  { value: 'CB4', display: 'CB4' },
  { value: 'CB5', display: 'CB5' },
];

// isDG options
export const isDGOptions = [
  { value: 'true', display: 'Yes' },
  { value: 'false', display: 'No' },
];

// isDGaccessible options
export const isDGaccessibleOptions = [
  { value: 'true', display: 'Yes' },
  { value: 'false', display: 'No' },
];

export const isConsigneeAddressResidentialOptions = [
  { value: 'true', display: 'Yes' },
  { value: 'false', display: 'No' },
];

export const isShipperAddressResidentialOptions = [
  { value: 'true', display: 'Yes' },
  { value: 'false', display: 'No' },
];

export const dgQuantityUnitOptions = [
  { value: 'KG', display: 'KG' },
  { value: 'LB', display: 'LB' },
  { value: 'LT', display: 'LT' },
];

export const emiratesInformationValidation = (isSaudiArabia: boolean) =>
  Yup.object().shape({
    type: Yup.string().required('Type is required'),

    accountNo: Yup.mixed()
      .test('is-valid', 'Account is required', (value: any) => {
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'object') return true;
        return !!value;
      })
      .required('Account is required'),

    serviceType: Yup.string()
      .transform(value => (Array.isArray(value) ? '' : value))
      .required('Service Type is required'),

    paymentMode: Yup.string()
      .transform(value => (Array.isArray(value) ? '' : value))
      .required('Payment Mode is required'),

    shipmentPurpose: Yup.string()
      .transform(value => (Array.isArray(value) ? '' : value))
      .required('Shipment Purpose is required'),

    shipmentType: Yup.string()
      .transform(value => (Array.isArray(value) ? '' : value))
      .required('Shipment Type is required'),

    isDG: Yup.boolean().default(false).required('DG selection is required'),

    isDGaccessible: Yup.boolean().default(false).required('DG Accessible selection is required'),

    shipperId: Yup.string().optional(),

    countryOfManufacture: Yup.string().required('Country of Manufacture is required'),

    // ✅ DG fields — TypeScript SAFE conditional validation

    dgUnId: Yup.string().when('isDG', ([isDG], schema) =>
      isDG
        ? schema
            .required('DG UN ID is required')
            .min(1, 'DG UN ID is required')
            .max(50, 'DG UN ID cannot exceed 50 characters')
        : schema.notRequired(),
    ),

    dgPiId: Yup.string().when('isDG', ([isDG], schema) =>
      isDG
        ? schema
            .required('DG PI ID is required')
            .min(1, 'DG PI ID is required')
            .max(50, 'DG PI ID cannot exceed 50 characters')
        : schema.notRequired(),
    ),

    dgProperShippingName: Yup.string().when('isDG', ([isDG], schema) =>
      isDG
        ? schema
            .required('Proper Shipping Name is required')
            .min(1, 'Proper Shipping Name is required')
            .max(200, 'Proper Shipping Name cannot exceed 200 characters')
        : schema.notRequired(),
    ),

    dgClassDivision: Yup.string().when('isDG', ([isDG], schema) =>
      isDG
        ? schema
            .required('Class/Division is required')
            .min(1, 'Class/Division is required')
            .max(50, 'Class/Division cannot exceed 50 characters')
        : schema.notRequired(),
    ),

    dgPackingGroup: Yup.string().when('isDG', ([isDG], schema) =>
      isDG
        ? schema
            .required('Packing Group is required')
            .min(1, 'Packing Group is required')
            .max(50, 'Packing Group cannot exceed 50 characters')
        : schema.notRequired(),
    ),

    dgQuantity: Yup.string().when('isDG', ([isDG], schema) =>
      isDG
        ? schema
            .required('Quantity is required')
            .test('is-valid-number', 'Must be a valid positive number', value => {
              if (!value) return false;
              const num = parseFloat(value);
              return !isNaN(num) && num >= 0;
            })
            .test('max-decimals', 'Maximum 3 decimal places allowed', value => {
              if (!value) return true;
              const decimalPart = value.split('.')[1];
              return !decimalPart || decimalPart.length <= 3;
            })
            .test('max-value', 'Quantity cannot exceed 999999.999', value => {
              if (!value) return true;
              const num = parseFloat(value);
              return num <= 999999.999;
            })
        : schema.notRequired(),
    ),

    dgQuantityUnit: Yup.string().when('isDG', ([isDG], schema) =>
      isDG ? schema.required('Quantity Unit is required').min(1) : schema.notRequired(),
    ),

    locationIdentifier: isSaudiArabia
      ? Yup.string().required('Location Identifier is required')
      : Yup.string().notRequired(),

    nationalId: isSaudiArabia ? Yup.string().required('National ID is required') : Yup.string().notRequired(),

    dateOfBirth: isSaudiArabia
      ? Yup.date().nullable().required('Date of Birth is required')
      : Yup.date().nullable().notRequired(),

    nationality: isSaudiArabia ? Yup.string().required('Nationality is required') : Yup.string().notRequired(),

    expiryDate: isSaudiArabia
      ? Yup.date().nullable().required('Expiry Date is required')
      : Yup.date().nullable().notRequired(),

    nameAsPerID: isSaudiArabia ? Yup.string().required('Name as per ID is required') : Yup.string().notRequired(),
  });

export const checkImageType = (imageType: string) => /image*/.test(imageType);

export const ImageOptions: DropdownOptionType[] = [
  { value: 'SIGNATURE', display: 'Signature' },
  { value: 'LETTERHEAD', display: 'Letterhead' },
];

export const DocumentOptions: DropdownOptionType[] = [
  { value: 'CommercialInvoice', display: 'Commercial Invoice' },
  { value: 'Photo', display: 'Photo' },
  { value: 'PackingList', display: 'Packing List' },
  { value: 'CertifcateOfOrigin', display: 'Certificate Of Origin' },
  { value: 'ShipperDGD', display: 'Shipper DGD' },
  { value: 'Other', display: 'Other' },
  { value: 'CustomsClearanceExport', display: 'Customs Clearance Export' },
  { value: 'CustomsClearanceImport', display: 'Customs Clearance Import' },
];

export interface emiratesInformationSchema {
  csp: string;
  type: string;
  accountNo: string;
  dateOfBirth?: Dayjs | null;
  expiryDate?: Dayjs | null;
  serviceType: string;
  paymentMode: string;
  countryOfManufacture: string;
}

export const onForward = async ({
  values,
  shipmentNumber,
  dispatch,
  nextFunc,
}: {
  values: emiratesInformationSchema;
  shipmentNumber: string;
  dispatch: AppDispatch;
  nextFunc: () => void;
}) => {
  try {
    const body = { ...JSON.parse(JSON.stringify(values)) };
    body.csp = 'emirates';

    for (const key in body) {
      if (body[key]) {
        if (key === 'accountNo') {
          body[key] = body[key]?.split('-')?.[0];
        }
        if (key === 'expiryDate') {
          body[key] = dayjs(values?.expiryDate).format('YYYY-MM-DD');
        }
        if (key === 'dateOfBirth') {
          body[key] = dayjs(values?.dateOfBirth).format('YYYY-MM-DD');
        }
      }
    }
    await dispatch(
      postFedExDetails({
        shipmentNumber: `${shipmentNumber}`,
        body,
        date: null,
        nextFunc,
      }),
    );
  } catch (error) {
    console.error('Forward Failed', error);
  }
};
